import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Alert,
  LinearProgress,
  Chip,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  Paper
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Info as InfoIcon
} from '@mui/icons-material';

const AmazonUpload = ({ onUploadSuccess }) => {
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [lastUpload, setLastUpload] = useState(null);

  useEffect(() => {
    fetchLastUpload();
  }, []);

  const fetchLastUpload = async () => {
    try {
      const response = await fetch('/api/returns/amazon/last-upload');
      const data = await response.json();
      if (data.success && data.data.hasUploads) {
        setLastUpload(data.data);
      }
    } catch (error) {
      console.error('Error fetching last upload:', error);
    }
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleString();
    } catch (error) {
      return dateString;
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleUpload(files[0]);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleUpload(file);
    }
  };

  const handleUpload = async (file) => {
    if (!file) return;

    // Validate file type
    const validTypes = ['text/xml', 'application/xml', 'text/plain'];
    const validExtensions = ['.xml', '.txt'];
    const isValidType = validTypes.includes(file.type) || 
                       validExtensions.some(ext => file.name.toLowerCase().endsWith(ext));
    
    if (!isValidType) {
      setError('Please select a valid XML or TSV file (.xml or .txt)');
      return;
    }

    setUploading(true);
    setError(null);
    setResult(null);

    const formData = new FormData();
    formData.append('xmlFile', file);

    try {
      const response = await fetch('/api/returns/amazon/upload', {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        setResult(data);
        // Refresh last upload timestamp
        fetchLastUpload();
        // Notify parent component to refresh returns list
        if (onUploadSuccess) {
          onUploadSuccess(data);
        }
      } else {
        setError(data.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError('Failed to upload file. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
          <UploadIcon sx={{ mr: 1, color: '#FF9900' }} />
          Amazon Returns File Upload
        </Typography>

        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Instructions:</strong> Download your Amazon Returns report from Seller Central in XML or Tab-delimited format, then upload it below to integrate with your Returns Manager.
          </Typography>
        </Alert>

        {/* Upload Area */}
        <Paper
          sx={{
            border: `2px dashed ${dragActive ? '#1976d2' : '#ccc'}`,
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            backgroundColor: dragActive ? 'rgba(25, 118, 210, 0.04)' : 'transparent',
            cursor: 'pointer',
            mb: 2
          }}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={() => document.getElementById('amazon-file-input').click()}
        >
          <UploadIcon sx={{ fontSize: 48, color: '#FF9900', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Drag & drop your Amazon returns file here
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            or click to browse files
          </Typography>
          <Box sx={{ mt: 2 }}>
            <Chip label="XML" variant="outlined" sx={{ mr: 1 }} />
            <Chip label="TSV/TXT" variant="outlined" />
          </Box>
          
          <input
            id="amazon-file-input"
            type="file"
            accept=".xml,.txt"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
        </Paper>

        {/* Loading */}
        {uploading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="body2" sx={{ mt: 1, textAlign: 'center' }}>
              Uploading and parsing Amazon returns data...
            </Typography>
          </Box>
        )}

        {/* Error */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} icon={<ErrorIcon />}>
            {error}
          </Alert>
        )}

        {/* Success Result */}
        {result && (
          <Alert severity="success" sx={{ mb: 2 }} icon={<SuccessIcon />}>
            <Typography variant="body1" gutterBottom>
              <strong>Success!</strong> Processed {result.data.returnsProcessed} Amazon returns from {result.data.filename}
            </Typography>
            
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Paper sx={{ p: 2, bgcolor: 'rgba(76, 175, 80, 0.04)' }}>
                  <Typography variant="subtitle2" gutterBottom>
                    <InfoIcon sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                    File Details
                  </Typography>
                  <List dense>
                    <ListItem disablePadding>
                      <ListItemText primary="Filename" secondary={result.data.filename} />
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemText primary="Format" secondary={result.data.format} />
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemText primary="Size" secondary={`${(result.data.fileSize / 1024).toFixed(1)} KB`} />
                    </ListItem>
                    <ListItem disablePadding>
                      <ListItemText primary="Returns Processed" secondary={result.data.returnsProcessed} />
                    </ListItem>
                  </List>
                </Paper>
              </Grid>
              
              {result.data.returns && result.data.returns.length > 0 && (
                <Grid item xs={12} md={6}>
                  <Paper sx={{ p: 2, bgcolor: 'rgba(76, 175, 80, 0.04)' }}>
                    <Typography variant="subtitle2" gutterBottom>
                      Sample Returns Data
                    </Typography>
                    <List dense>
                      {result.data.returns.slice(0, 3).map((returnItem, index) => (
                        <ListItem key={index} disablePadding>
                          <ListItemText
                            primary={`${returnItem.return_number}`}
                            secondary={
                              <span>
                                <strong>Order:</strong> {returnItem.order_id}<br />
                                <strong>Product:</strong> {returnItem.product_name || 'N/A'}<br />
                                <strong>Reason:</strong> {returnItem.return_reason}
                              </span>
                            }
                          />
                        </ListItem>
                      ))}
                      {result.data.returns.length > 3 && (
                        <ListItem disablePadding>
                          <ListItemText secondary={`... and ${result.data.returns.length - 3} more returns`} />
                        </ListItem>
                      )}
                    </List>
                  </Paper>
                </Grid>
              )}
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Alert severity="info" variant="outlined">
              <Typography variant="body2">
                <strong>Next Steps:</strong><br />
                • Returns data has been added to your system<br />
                • Visit the All Returns page to see the uploaded data<br />
                • Data will appear with "Amazon Upload" platform badge
              </Typography>
            </Alert>
          </Alert>
        )}

        {/* Manual Upload Button as fallback */}
        <Box sx={{ textAlign: 'center', mt: 2 }}>
          <Button
            variant="contained"
            component="label"
            startIcon={<UploadIcon />}
            disabled={uploading}
            sx={{
              bgcolor: '#FF9900',
              '&:hover': { bgcolor: '#E88600' }
            }}
          >
            Choose Amazon Returns File
            <input
              type="file"
              accept=".xml,.txt"
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </Button>
          
          {/* Last Upload Timestamp */}
          {lastUpload && (
            <Typography 
              variant="caption" 
              color="textSecondary" 
              sx={{ 
                display: 'block', 
                mt: 1, 
                fontSize: '0.75rem',
                fontStyle: 'italic'
              }}
            >
              Last upload: {formatDateTime(lastUpload.lastUploadDate)}
            </Typography>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default AmazonUpload;